import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExamTimeTablePDF from "./ExamTimeTablePDF";
import ActionsMenu from "./ActionsMenu";

export default function ExamTimeTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [timetableDetails, setTimetableDetails] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  // Edit dialog state (for editing a single date)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null); // timetable id
  const [editingDateInput, setEditingDateInput] = useState(""); // yyyy-mm-dd for input
  // Edit dialog state (for editing exam time)
  const [descEditDialogOpen, setDescEditDialogOpen] = useState(false);
  const [editingDescRowId, setEditingDescRowId] = useState(null); // timetable id
  const [editingDescInput, setEditingDescInput] = useState(""); // text input for exam time
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [collegeLogo, setCollegeLogo] = useState(null);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // --- helpers for date formatting ---
  const pad = (n) => String(n).padStart(2, "0");

  // format ISO/date -> DD-MM-YYYY (hyphens)
  const formatIsoToDDMMYYYY = (isoOrDateStr) => {
    if (!isoOrDateStr) return "";
    const d = new Date(isoOrDateStr);
    if (isNaN(d)) return "";
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  };

  // convert DD-MM-YYYY or DD/MM/YYYY -> yyyy-mm-dd for <input type="date">
  const formattedToInput = (formatted) => {
    if (!formatted) return "";
    const sep = formatted.includes("-") ? "-" : "/";
    const [dd, mm, yyyy] = formatted.split(sep);
    if (!dd || !mm || !yyyy) return "";
    return `${yyyy}-${mm}-${dd}`;
  };

  // convert yyyy-mm-dd (input) -> DD-MM-YYYY
  const inputToFormatted = (inputStr) => {
    if (!inputStr) return "";
    const [yyyy, mm, dd] = inputStr.split("-");
    if (!dd || !mm || !yyyy) return "";
    return `${dd}-${mm}-${yyyy}`;
  };

  // Fetch Timetables (main grid)
  const fetchTimetables = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/exam-timetables/${user.client_id}/${user.college_id}`
      );

      if (data.success) {
        const map = new Map();
        data.data.items.forEach((r) => {
          const key = `${r.college_id}-${r.program?.program_name}-${r.branch?.branch_name}-${r.exam_schedule_master.exam_schedule_master_id}`;
          if (!map.has(key)) {
            map.set(key, {
              id: key,
              exam_schedule_master_id: r.exam_schedule_master.exam_schedule_master_id || "",
              college_id: r.college_id,
              program_id: r.program_id,
              branch_id: r.branch_id,
              program_name: r.program?.program_name || "",
              branch_name: r.branch?.branch_name || "",
              exam_type_name: r.exam_schedule_master?.exam_type?.exam_type_name || "",
              exam_month: r.exam_schedule_master?.exam_month || "",
              exam_year: r.exam_schedule_master?.exam_year || "",
              year_number: r.exam_schedule_detail?.year_number || "",
              semester_number: r.exam_schedule_detail?.semester_number || "",
              exam_schedule_detail_id: r.exam_schedule_detail?.exam_schedule_detail_id || "",
              created_on: r.created_on ? new Date(r.created_on).toLocaleDateString("en-IN") : "",
            });
          }
        });

        setRows(Array.from(map.values()));
      } else {
        showSnackbar(data.message || "Failed to load timetables", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to load timetables", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimetables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search filter
  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.program_name.toLowerCase().includes(s) ||
        r.branch_name.toLowerCase().includes(s) ||
        r.exam_type_name.toLowerCase().includes(s) ||
        r.exam_month.toLowerCase().includes(s) ||
        r.exam_year.toString().includes(s)
    );
  }, [search, rows]);

  // View timetable details (details dialog)
  const handleViewDetails = async (row) => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/exam-timetables/${user.client_id}/${user.college_id}/${row.program_id}/${row.exam_schedule_detail_id}/${row.branch_id}/${row.year_number}/${row.semester_number}`,
        { headers: { "Cache-Control": "no-cache" } }
      );

      setCollegeInfo(data.data.items[0].college);

      if (data.data.items[0].college.college_logopath) {
        const img = new Image();
        //img.crossOrigin = "Anonymous"; // needed for CORS images
        img.src = `${process.env.REACT_APP_API_BASE_URL}/uploads/logos/${user.client_id}/${data.data.items[0].college.society_id}/${data.data.items[0].college.college_id}/${data.data.items[0].college.college_logopath}`;
        img.onload = () => {
          //setCollegeLogo(img);
        };
      }

      const flatData = (data.data.items || [])
        .map((item) => ({
          id: String(item.timetable_id),
          timetable_id: item.timetable_id,
          program_name: item.program?.program_name ?? "",
          branch_name: item.branch?.branch_name ?? "",
          course_code: item.exam_course?.course_code ?? "",
          short_code: item.exam_course?.short_code ?? "",
          course_name: item.exam_course?.course_name ?? "",
          from_exam_date: formatIsoToDDMMYYYY(item.from_exam_date), // DD-MM-YYYY
          description: item.exam_slot?.description ?? "",
          exam_slot_desc: item.exam_slot_desc ?? "",
          year_number: item.exam_schedule_detail?.year_number ?? "",
          semester_number: item.exam_schedule_detail?.semester_number ?? "",
        }))
        .sort((a, b) => {
          const [da, ma, ya] = a.from_exam_date.split("-");
          const [db, mb, yb] = b.from_exam_date.split("-");

          return new Date(`${ya}-${ma}-${da}`) - new Date(`${yb}-${mb}-${db}`);
        });

      setTimetableDetails(flatData);
      setSelectedTimetable(row);
      setDialogOpen(true);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load timetable details", "error");
    }
  };

  // Open the edit dialog for a particular row
  const openEditDialog = (row) => {
    setEditingRowId(row.id);
    setEditingDateInput(formattedToInput(row.from_exam_date)); // yyyy-mm-dd
    setEditDialogOpen(true);
  };

  // Save the edited date: update local grid and send PUT to backend
  const handleSaveEditedDate = async () => {
    if (!editingRowId) {
      setEditDialogOpen(false);
      return;
    }

    const rawInput = editingDateInput; // yyyy-mm-dd
    if (!rawInput) {
      showSnackbar("Please choose a valid date", "error");
      return;
    }

    const formatted = inputToFormatted(rawInput); // dd-mm-yyyy

    // optimistic UI update: update local state
    setTimetableDetails((prev) =>
      prev.map((r) => (r.id === editingRowId ? { ...r, from_exam_date: formatted } : r))
    );

    setEditDialogOpen(false);

    try {
      // send to backend (adjust route if your API differs)
      await axios.put(
        `${API_BASE_URL}/api/exam-timetables/date/${user.client_id}/${user.college_id}/${editingRowId}`,
        {
          from_exam_date: rawInput, // send yyyy-mm-dd or whichever your backend expects
        }
      );

      showSnackbar("Date updated successfully", "success");
    } catch (err) {
      // rollback local change on error (optional: refetch details instead)
      console.error("Update failed", err);
      showSnackbar("Failed to save date to server", "error");

      // rollback by reloading details from server (safer)
      if (selectedTimetable) {
        handleViewDetails(selectedTimetable);
      }
    } finally {
      setEditingRowId(null);
      setEditingDateInput("");
    }
  };
  // Open the edit dialog for exam time (description)
  const openDescEditDialog = (row) => {
    setEditingDescRowId(row.id);
    setEditingDescInput(row.description || "");
    setDescEditDialogOpen(true);
  };

  // Cancel editing exam time
  const handleCancelDescEdit = () => {
    setDescEditDialogOpen(false);
    setEditingDescRowId(null);
    setEditingDescInput("");
  };

  // Save the edited exam time
  const handleSaveEditedDesc = async () => {
    if (!editingDescRowId) {
      setDescEditDialogOpen(false);
      return;
    }

    const newDesc = editingDescInput.trim();
    if (!newDesc) {
      showSnackbar("Please enter a valid exam time", "error");
      return;
    }

    // optimistic update
    setTimetableDetails((prev) =>
      prev.map((r) => (r.id === editingDescRowId ? { ...r, description: newDesc } : r))
    );

    setDescEditDialogOpen(false);

    try {
      await axios.put(
        `${API_BASE_URL}/api/exam-timetables/description/${user.client_id}/${user.college_id}/${editingDescRowId}`,
        {
          description: newDesc,
        }
      );

      showSnackbar("Exam time updated successfully", "success");
    } catch (err) {
      console.error("Update failed", err);
      showSnackbar("Failed to save exam time to server", "error");

      // rollback by refetching
      if (selectedTimetable) {
        handleViewDetails(selectedTimetable);
      }
    } finally {
      setEditingDescRowId(null);
      setEditingDescInput("");
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingRowId(null);
    setEditingDateInput("");
  };

  // Columns for main grid
  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
        valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
      },
      {
        field: "exam_schedule_master_id",
        headerName: "Id",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (
          <span
            style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setSelectedMasterId(params.row.exam_schedule_master_id)}
          >
            {params.row.exam_schedule_master_id}
          </span>
        ),
      },
      { field: "program_name", headerName: "Program", flex: 1, minWidth: 150 },
      { field: "branch_name", headerName: "Branch", flex: 1, minWidth: 250 },
      { field: "exam_type_name", headerName: "Exam Type", flex: 1, minWidth: 100 },
      { field: "exam_month", headerName: "MonthYear", flex: 1, minWidth: 150 },
      { field: "year_number", headerName: "Year", flex: 0.5, minWidth: 80 },
      { field: "semester_number", headerName: "Semester", flex: 1, minWidth: 100 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => (
          <ActionsMenu
            row={params.row}
            user={user}
            handleGenerateHalltickets={handleGenerateHalltickets}
            handleViewDetails={handleViewDetails}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const handleGenerateHalltickets = async (row) => {
    try {
      if (row.exam_type_name == "REGULAR") {
        const { data } = await axios.post(
          `${API_BASE_URL}/api/exam-halltickets/${user.client_id}/${row.college_id}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.branch_id}/${row.exam_type_name}/generateregular`,
          {
            created_by: user.user_email, // or whoever is logged in
          },
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              "If-Modified-Since": "0",
            },
          }
        );
        if (data.success) {
          showSnackbar("Halltickets generated successfully!", "success");
        } else {
          showSnackbar(data.message || "Failed to generate halltickets", "error");
        }
      } else {
        const { data } = await axios.post(
          `${API_BASE_URL}/api/exam-halltickets/${user.client_id}/${row.college_id}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.branch_id}/${row.exam_type_name}/generatesupply`,
          {
            created_by: user.user_email, // or whoever is logged in
          },
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              "If-Modified-Since": "0",
            },
          }
        );
        if (data.success) {
          showSnackbar("Halltickets generated successfully!", "success");
        } else {
          showSnackbar(data.message || "Failed to generate halltickets", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Error generating halltickets", "error");
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const topY = 15;
    const logoWidth = 30;
    const logoHeight = 30;
    let y = topY + 8;

    const toRoman = (num) => {
      const romans = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
      return romans[num] || num || "-";
    };
    // ============= HEADER =============
    if (collegeInfo) {
      let textX = margin;
      let align = "left";

      if (collegeLogo) {
        // Draw logo on left
        doc.addImage(collegeLogo, "PNG", margin, topY, logoWidth, logoHeight);
        textX = margin + logoWidth + 10;
        align = "left";
      } else {
        // Center text if no logo
        textX = pageWidth / 2;
        align = "center";
      }

      // College Name
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(collegeInfo.college_name || "", textX, y, { align });

      // Reset font for smaller text
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      // Collect details
      let details = [];
      if (collegeInfo.is_autonomous !== undefined) {
        const autoText = collegeInfo.is_autonomous ? "AUTONOMOUS" : "AFFILIATED";
        details.push(autoText);
      }
      if (collegeInfo.affiliation) details.push(` ${collegeInfo.affiliation}`);
      if (collegeInfo.accreditation) details.push(` ${collegeInfo.accreditation}`);

      // Print details
      if (details.length > 0) {
        y += 6;
        doc.text(details.join(" , "), textX, y, { align });
      }

      // Address
      const address = [
        collegeInfo.college_location,
        collegeInfo.college_district,
        collegeInfo.college_state,
        collegeInfo.college_pincode,
      ]
        .filter(Boolean)
        .join(", ");

      if (address) {
        y += 6;
        doc.text(address, textX, y, { align });
      }
    }

    // Draw a line below header
    y += 8;
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);

    // ============= TIMETABLE SECTION =============
    const yearNum = toRoman(selectedTimetable.year_number) ?? "";
    const semesterNum = toRoman(selectedTimetable.semester_number) ?? "";
    let startY = y + 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const title = `TIME TABLE FOR ${yearNum} YEAR ${semesterNum} SEMESTER  ${
      selectedTimetable?.exam_month || ""
    } - ${selectedTimetable?.exam_type_name || ""} EXAMINATIONS`;

    doc.text(title.trim(), pageWidth / 2, startY, {
      align: "center",
      maxWidth: pageWidth - 20, // ensures long text wraps instead of cutting off
    });

    startY += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Program: ${selectedTimetable?.program_name || ""}`, margin, startY);
    doc.text(`Branch: ${selectedTimetable?.branch_name || ""}`, pageWidth / 2 + 20, startY);

    const tableColumn = ["SNo", "Course Code", "Course Name", "Exam Date", "Exam Time"];
    const tableRows = timetableDetails.map((row, index) => [
      index + 1,

      row.course_code,
      row.course_name,
      row.from_exam_date,
      row.exam_slot_desc,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: startY + 15,
      styles: { fontSize: 9, halign: "left", valign: "middle" },
      headStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 15 }, // SNo
        1: { cellWidth: 30 }, // Course Code
        2: { cellWidth: 50 }, // Course Name
        3: { cellWidth: 25 }, // Exam Date
        4: { cellWidth: 60 }, // ✅ Wider Exam Time column
      },
    });
    const tableEndY = doc.lastAutoTable?.finalY + 20 ?? startY + 20; // fallback
    doc.setFontSize(11);
    doc.text(
      "Signature of the Principal",
      pageWidth - 20,
      tableEndY + 10, // 10mm below table
      { align: "right" }
    );
    // ============= FILE NAME =============
    const firstRow = timetableDetails[0];
    const programName = firstRow?.program_name?.replace(/\s+/g, "_") || "Program";
    const branchName = firstRow?.branch_name?.replace(/\s+/g, "_") || "Branch";
    const yearNumber = firstRow?.year_number || "-";
    const semesterNumber = firstRow?.semester_number || "-";
    const fileName = `TimeTable_${programName}_${branchName}_${yearNumber}_Year_${semesterNumber}_Semester.pdf`;

    doc.save(fileName);
  };

  // Function to export CSV
  const handleDownloadCSV = () => {
    const header = [
      "S.No",
      "Program",
      "Branch",
      "Course Code",
      "Short Code",
      "Course Name",
      "Exam Date",
      "Exam Time",
    ];

    const rows = timetableDetails.map((row, index) => [
      index + 1,
      row.program_name,
      row.branch_name,
      row.course_code,
      row.short_code,
      row.course_name,
      row.from_exam_date,
      row.exam_slot_desc,
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," + [header, ...rows].map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "timetable.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            EXAM TIMETABLES & HALL TICKETS
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Render ExamTimeTablePDF when a master id is selected.
              We pass an onDownloaded callback to clear selectedMasterId so
              the parent will unmount the component after download. */}
          {selectedMasterId && (
            <ExamTimeTablePDF
              clientId={user.client_id}
              examScheduleMasterId={selectedMasterId}
              onDownloaded={() => {
                // clear selectedMasterId so the component unmounts.
                // this ensures selecting a new row will mount it fresh and show the button again.
                setSelectedMasterId(null);
              }}
            />
          )}
        </Toolbar>

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            autoHeight
            pageSize={pageSize} // controlled pageSize
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
            sx={{
              // ensure DataGrid internal scroller allows horizontal scroll on small screens
              minWidth: 900,
            }}
          />
        </Box>
      </Paper>

      {/* Timetable Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          TIMETABLE DETAILS FOR <strong>{selectedTimetable?.exam_type_name}</strong> -{" "}
          <strong>{selectedTimetable?.exam_month}</strong>
          {timetableDetails?.length > 0 && (
            <>
              {" "}
              | <strong>{timetableDetails[0].program_name}</strong> -{" "}
              <strong>{timetableDetails[0].branch_name}</strong>
            </>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleDownloadPDF}
              color="secondary"
              variant="outlined"
              sx={{ color: "green" }}
            >
              PDF
            </Button>
            <Button
              onClick={handleDownloadCSV}
              color="secondary"
              variant="outlined"
              sx={{ color: "green" }}
            >
              CSV
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <DataGrid
              rows={timetableDetails}
              getRowId={(row) => row.id}
              columns={[
                {
                  field: "sno",
                  headerName: "SNo",

                  width: 40,
                  valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
                },
                { field: "course_code", headerName: "Course code", width: 120 },
                { field: "short_code", headerName: "Short code", width: 120 },
                { field: "course_name", headerName: "Course Name", flex: 1, minWidth: 180 },
                {
                  field: "from_exam_date",
                  headerName: "Exam Date",
                  flex: 0.6,
                  minWidth: 100,
                  // render cell with Edit button that opens a dialog
                  renderCell: (params) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem" }}>{params.value}</Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          openEditDialog(params.row);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  ),
                },
                {
                  field: "exam_slot_desc",
                  headerName: "Exam Time",
                  flex: 1,
                  minWidth: 150,
                  // render cell with Edit button that opens a dialog
                  renderCell: (params) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem" }}>{params.value}</Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          openDescEditDialog(params.row);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  ),
                },
              ]}
              autoHeight
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
              sx={{ minWidth: 900 }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Date Confirmation Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit}>
        <DialogTitle>Edit Exam Date</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Choose date"
              type="date"
              value={editingDateInput}
              onChange={(e) => setEditingDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button
            onClick={handleSaveEditedDate}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={descEditDialogOpen} onClose={handleCancelEdit}>
        <DialogTitle>Edit Exam Time</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Type ExamTime"
              type="text"
              value={editingDescInput}
              onChange={(e) => setEditingDescInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDescEdit}>Cancel</Button>
          <Button
            onClick={handleSaveEditedDesc}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
